import { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import HeroSection from '@/components/HeroSection';
import Leaderboard from '@/components/Leaderboard';
import RealtimeTicker from '@/components/RealtimeTicker';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import CurrencySwitcher from '@/components/CurrencySwitcher';
import SEO from '@/components/SEO';
import { useDonations } from '@/hooks/useDonations';
import { formatAmount } from '@/utils/formatAmount';

// Lazy load modal - only needed when user clicks donate
const DonationModal = lazy(() => import('@/components/DonationModal'));

const Index = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    donations,
    totalAmount,
    loading,
    latestDonation,
    addDonation,
    getHighestAllTime,
    getMonthlyHighest,
    getNewestDonation,
  } = useDonations();

  // Handle ?open=donate query parameter
  useEffect(() => {
    if (searchParams.get('open') === 'donate') {
      setIsModalOpen(true);
      // Clean up the URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  // Build dynamic, content-rich JSON-LD: ItemList of top mongos for richer SERP signals
  const homeJsonLd = useMemo(() => {
    const top = (donations || []).slice(0, 10);
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Top Mongos – DUMONGO Leaderboard',
        url: 'https://dumongo.com/',
        numberOfItems: top.length,
        itemListElement: top.map((d, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: d.display_name,
          description: `${formatAmount(Number(d.amount), d.currency)} burned for nothing.`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dumongo.com/' },
        ],
      },
    ];
  }, [donations]);

  return (
    <div className="min-h-screen bg-background pb-16">
      <SEO path="/" jsonLd={homeJsonLd} />
      {/* Header with Switchers */}
      <header role="banner" className="fixed top-0 right-0 z-50 flex items-center gap-2 p-3 sm:p-4 bg-background/95 backdrop-blur-sm">
        <CurrencySwitcher />
        <LanguageSwitcher />
      </header>

      {/* Scanline overlay effect */}
      <div 
        className="pointer-events-none fixed inset-0 z-50 opacity-[0.03]"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, hsl(120 100% 55% / 0.1) 2px, hsl(120 100% 55% / 0.1) 4px)'
        }}
        aria-hidden="true"
      />

      {/* Main Content */}
      <main className="relative z-10" role="main">
        <HeroSection 
          donations={donations}
          onDonateClick={() => setIsModalOpen(true)}
          highestDonation={getHighestAllTime()}
        />
        
        <article aria-label={t('leaderboard.title')}>
          <Leaderboard
            donations={donations}
            loading={loading}
            highestAllTime={getHighestAllTime()}
            monthlyHighest={getMonthlyHighest()}
            newestDonation={getNewestDonation()}
          />
        </article>

        {/* Footer Credits */}
        <footer className="text-center py-8 text-xs text-muted-foreground space-y-3" role="contentinfo">
          <nav aria-label="Footer" className="flex justify-center gap-4 flex-wrap text-sm">
            <Link to="/about" className="hover:text-primary transition-colors">ABOUT</Link>
            <span aria-hidden="true">·</span>
            <Link to="/faq" className="hover:text-primary transition-colors">FAQ</Link>
            <span aria-hidden="true">·</span>
            <Link to="/press" className="hover:text-primary transition-colors">PRESS</Link>
          </nav>
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <p className="mt-1">{t('footer.artProject')}</p>
          <div className="mt-4 text-primary text-2xl font-mono tracking-widest opacity-60" aria-label="DUMONGO Logo">
            [ DM ]
          </div>
          {/* Hidden keywords for SEO */}
          <span className="sr-only">
            The Experiment. Join the Mongos. Nothing is Everything. The Leaderboard is watching.
            Invest in nothing. Become a legend. The dumbest highscore list on the internet.
            Burn money online. Satirical art project. Pointless donations. Highscore.
          </span>
        </footer>
      </main>

      {/* Donation Modal - Lazy loaded */}
      <Suspense fallback={null}>
        <DonationModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      </Suspense>

      {/* Realtime Ticker */}
      <RealtimeTicker 
        donations={donations} 
        latestDonation={latestDonation} 
      />
    </div>
  );
};

export default Index;
